import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { WORK_ORDER_KIND_LABELS } from '@wodi/domain';
import type { AssetListItemView, WorkOrderListItemView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  LoadingBlock,
  PageHeader,
  Row,
  Section,
  SegmentedControl,
  Table,
  TableBody,
  TableHeader,
} from '@wodi/ui';
import { useState } from 'react';

import {
  assetKeys,
  assetListQuery,
  maintenanceDueQuery,
  registerAsset,
  workOrderListQuery,
  type AssetFilter,
  type WorkOrderFilter,
} from '../api/assets.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { TableScroll } from '../layout/screen.tsx';
import { dateTimeText, LoadFailed, NotAllowed, OfflineListNotice } from '../operations/format.tsx';
import styles from '../operations/operations.module.css';
import { toasts } from '../toasts.ts';
import { AssetFormDialog } from './asset-form.tsx';
import { AssetStatusTag, DueWords, WorkOrderStatusTag } from './words.tsx';

// Assets (docs/plans/phase3-operations-modules.md §3.3): the register by status, the
// maintenance due list and the work orders.

type View = 'register' | 'due' | 'work-orders';

function AssetTable({
  label,
  items,
  empty,
}: {
  label: string;
  items: AssetListItemView[];
  empty: string;
}) {
  return (
    <TableScroll>
      <Table flat aria-label={label}>
        <TableHeader>
          <Column isRowHeader>Tag</Column>
          <Column>Name</Column>
          <Column>Category</Column>
          <Column>Location</Column>
          <Column>Status</Column>
          <Column>Next service</Column>
          <Column>Open work</Column>
        </TableHeader>
        <TableBody items={items} renderEmptyState={() => empty}>
          {(a) => (
            <Row id={a.id}>
              <Cell>
                <Link to="/assets/$assetId" params={{ assetId: a.id }}>
                  <span className="wodi-id">{a.tag}</span>
                </Link>
              </Cell>
              <Cell>{a.name}</Cell>
              <Cell>{a.category.display}</Cell>
              <Cell>{a.location ?? 'Not recorded'}</Cell>
              <Cell>
                <AssetStatusTag status={a.status} />
              </Cell>
              <Cell>
                <DueWords nextServiceDue={a.nextServiceDue} dueState={a.dueState} />
              </Cell>
              <Cell>
                <span className={styles.numeric}>{String(a.openWorkOrders)}</span>
              </Cell>
            </Row>
          )}
        </TableBody>
      </Table>
    </TableScroll>
  );
}

export function WorkOrderTable({
  items,
  empty,
}: {
  items: WorkOrderListItemView[];
  empty: string;
}) {
  return (
    <TableScroll>
      <Table flat aria-label="Work orders">
        <TableHeader>
          <Column isRowHeader>Number</Column>
          <Column>Asset</Column>
          <Column>Kind</Column>
          <Column>Problem</Column>
          <Column>Opened</Column>
          <Column>Status</Column>
        </TableHeader>
        <TableBody items={items} renderEmptyState={() => empty}>
          {(w) => (
            <Row id={w.id}>
              <Cell>
                <Link to="/work-orders/$workOrderId" params={{ workOrderId: w.id }}>
                  <span className="wodi-id">{w.workOrderNumber}</span>
                </Link>
              </Cell>
              <Cell>
                <span className="wodi-id">{w.asset.tag}</span> {w.asset.name}
              </Cell>
              <Cell>{WORK_ORDER_KIND_LABELS[w.kind]}</Cell>
              <Cell>{w.problem}</Cell>
              <Cell>{dateTimeText(w.openedAt)}</Cell>
              <Cell>
                <WorkOrderStatusTag status={w.status} />
              </Cell>
            </Row>
          )}
        </TableBody>
      </Table>
    </TableScroll>
  );
}

function Register() {
  const [filter, setFilter] = useState<AssetFilter>('in_service');
  const list = useQuery(assetListQuery(filter));
  return (
    <Section
      title="Register"
      actions={
        <SegmentedControl
          aria-label="Which assets"
          value={filter}
          onChange={(v) => {
            setFilter(v as AssetFilter);
          }}
          options={[
            { id: 'in_service', label: 'In service' },
            { id: 'out_of_service', label: 'Out of service' },
            { id: 'condemned', label: 'Condemned' },
            { id: 'all', label: 'All' },
          ]}
        />
      }
    >
      {list.isPending ? <LoadingBlock label="Loading assets" rows={5} /> : null}
      {list.isError ? (
        <LoadFailed
          error={list.error}
          fallback="Assets couldn't be loaded. Try again."
          onRetry={() => void list.refetch()}
        />
      ) : null}
      {list.isSuccess ? (
        <AssetTable
          label="Assets"
          items={list.data.items}
          empty="No assets here yet. Register one to start."
        />
      ) : null}
    </Section>
  );
}

function Due() {
  const list = useQuery(maintenanceDueQuery);
  return (
    <Section
      title="Maintenance due"
      description="Planned services overdue or due within 14 days, the most urgent first."
    >
      {list.isPending ? <LoadingBlock label="Loading maintenance due" rows={4} /> : null}
      {list.isError ? (
        <LoadFailed
          error={list.error}
          fallback="The list couldn't be loaded. Try again."
          onRetry={() => void list.refetch()}
        />
      ) : null}
      {list.isSuccess ? (
        <AssetTable
          label="Maintenance due"
          items={list.data.items}
          empty="No planned service is due in the next 14 days."
        />
      ) : null}
    </Section>
  );
}

function WorkOrders() {
  const [filter, setFilter] = useState<WorkOrderFilter>('open');
  const list = useQuery(workOrderListQuery(filter));
  return (
    <Section
      title="Work orders"
      actions={
        <SegmentedControl
          aria-label="Which work orders"
          value={filter}
          onChange={(v) => {
            setFilter(v as WorkOrderFilter);
          }}
          options={[
            { id: 'open', label: 'Open' },
            { id: 'closed', label: 'Closed' },
            { id: 'all', label: 'All' },
          ]}
        />
      }
    >
      {list.isPending ? <LoadingBlock label="Loading work orders" rows={4} /> : null}
      {list.isError ? (
        <LoadFailed
          error={list.error}
          fallback="Work orders couldn't be loaded. Try again."
          onRetry={() => void list.refetch()}
        />
      ) : null}
      {list.isSuccess ? (
        <WorkOrderTable
          items={list.data.items}
          empty="No work orders here. Open one from an asset."
        />
      ) : null}
    </Section>
  );
}

export function AssetsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [view, setView] = useState<View>('register');
  const [registering, setRegistering] = useState(false);
  const register = useMutation({
    mutationFn: registerAsset,
    onSuccess: (asset) => {
      void queryClient.invalidateQueries({ queryKey: assetKeys.all });
      toasts.show({ title: `Asset registered, ${asset.tag}` });
      setRegistering(false);
      void navigate({ to: '/assets/$assetId', params: { assetId: asset.id } });
    },
  });
  if (session === null || !can(session, 'assets.read'))
    return <NotAllowed title="Assets" what="see the asset register" />;
  return (
    <div className={styles.page}>
      <PageHeader
        title="Assets"
        description="Equipment and its planned maintenance and repairs."
        {...(can(session, 'assets.manage')
          ? {
              actions: (
                <Button
                  variant="primary"
                  isDisabled={!online}
                  onPress={() => {
                    register.reset();
                    setRegistering(true);
                  }}
                >
                  Register asset
                </Button>
              ),
            }
          : {})}
      />
      <div className={styles.toolbar}>
        <SegmentedControl
          aria-label="Assets view"
          value={view}
          onChange={(v) => {
            setView(v as View);
          }}
          options={[
            { id: 'register', label: 'Register' },
            { id: 'due', label: 'Maintenance due' },
            { id: 'work-orders', label: 'Work orders' },
          ]}
        />
      </div>
      <OfflineListNotice online={online} />
      {view === 'register' ? <Register /> : view === 'due' ? <Due /> : <WorkOrders />}
      {registering ? (
        <AssetFormDialog
          title="Register asset"
          confirmLabel="Register asset"
          isPending={register.isPending}
          error={register.error}
          onSubmit={(body) => {
            register.mutate(body);
          }}
          onClose={() => {
            setRegistering(false);
          }}
        />
      ) : null}
    </div>
  );
}
